import '@effect/io/Effect';
import '@infl-ts/ui-advocate-setup/src/theme/global-styles.css';
import '@influitive/infl-fonts/fonts.css';
import 'react-aspect-ratio/aspect-ratio.css';
import 'stream-chat-react/dist/css/index.css';

import { Userpilot } from 'userpilot';
import { USERPILOT_TOKEN } from '../env';

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Error as ErrorPage } from '@infl-ts/ui-advocate-components';
import type { ApolloQueryError } from '@infl-ts/ui-advocate-setup';
import { initializeApollo, SetupApp } from '@infl-ts/ui-advocate-setup';
import { DataDog } from '@infl-ts/util-datadog';
import { throwFn } from '@infl-ts/util-function';
import { logout, noMemoRequest as hubRequest } from '@infl-ts/util-hub-axios-instance';
import * as A from 'fp-ts/Array';
import { constant, constVoid, pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import * as IOE from 'fp-ts/IOEither';
import * as O from 'fp-ts/Option';
import type { ServerResponse } from 'http';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { FilledContext } from 'react-helmet-async';
import { HelmetProvider } from 'react-helmet-async';
import type { StaticRouterContext } from 'react-router';
import { BrowserRouter, StaticRouter } from 'react-router-dom';

import { DD_APP_ID, DD_CLIENT_TOKEN, DEPLOY_ENV, RELEASE_VERSION } from '../env';

declare global {
  interface Window {
    _infl?: {
      currentContact?: {
        id: string;
        name: string;
        email: string;
        isAdmin: boolean;
      };
      hub?: {
        id: string;
        subdomain?: string;
        features?: string[];
      };
    };
  }
}

type CustomAppProps = {
  host?: string;
  deviceType?: string;
  apolloClient?: ApolloClient<NormalizedCacheObject>;
  apolloState?: NormalizedCacheObject;
  routerContext?: StaticRouterContext;
};

const unitIOE = IOE.right(constVoid());
const abortIOE = IOE.left(O.none);

const setStatusCode = (res: ServerResponse, statusCode: number | undefined) =>
  typeof statusCode === 'undefined'
    ? unitIOE
    : IOE.rightIO(() => {
        res.statusCode = statusCode;
      });
const setHeader = (
  res: ServerResponse,
  name: string,
  value: number | string | ReadonlyArray<string>
) =>
  IOE.rightIO(() => {
    res.setHeader(name, value);
  });
const sendResponse = (res: ServerResponse) =>
  IOE.rightIO(() => {
    res.end();
  });

const redirectWithUrl = (
  res: ServerResponse,
  statusCode: number | undefined,
  redirectUrl: string | undefined
): IOE.IOEither<O.Option<never>, void> =>
  typeof redirectUrl === 'undefined'
    ? unitIOE
    : pipe(
        setStatusCode(res, statusCode ?? 307),
        IOE.apSecond(setHeader(res, 'Location', redirectUrl)),
        IOE.apSecond(sendResponse(res)),
        IOE.apSecond(abortIOE)
      );

const helmetContext: FilledContext | object = {};

const isHelmetContextFilled = (ctx: object): ctx is FilledContext =>
  Object.keys(ctx).includes('helmet');

const SSRRouter = ({
  routerContext,
  children
}: {
  routerContext?: StaticRouterContext;
  children: ReactNode;
}) => {
  const router = useRouter();
  return (
    <StaticRouter context={routerContext} location={router.asPath}>
      {children}
    </StaticRouter>
  );
};

const router = (routerContext?: StaticRouterContext) => {
  const Router = ({ children }: { children?: ReactNode }) => {
    const router = useRouter();

    useEffect(() => {
      if (typeof window !== 'undefined' && window?._infl?.currentContact) {
        Userpilot.reload();
      }
    }, [router.events]);

    return typeof window !== 'undefined' ? (
      <BrowserRouter>{children}</BrowserRouter>
    ) : (
      <SSRRouter routerContext={routerContext}>{children}</SSRRouter>
    );
  };
  Router.displayName = 'Router';
  return Router;
};

const logoutCallback = (apolloErrors?: ApolloQueryError[]) =>
  pipe(
    logout(false),
    redirectUrl => apolloErrors && apolloErrors.push({ statusCode: 307, redirectUrl }),
    constVoid
  );

const FallbackError = ({ error }: { error: Error }) => (
  <ErrorPage showReloadPage errorToLog={error} headerSize="h3" />
);

export const App = ({
  Component,
  pageProps,
  host,
  deviceType,
  apolloClient,
  apolloState,
  routerContext
}: AppProps & CustomAppProps): JSX.Element => {
  // TODO: Consider moving apollo client into SetupApp to be in same place as QueryClient
  const client = useMemo(
    () =>
      apolloClient ??
      initializeApollo({ host, initialState: apolloState, authErrorCallback: logoutCallback })(),
    [apolloClient, host, apolloState]
  );

  const [inflData, setInflData] = useState(null);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    if (!window._infl) {
      void pipe(
        hubRequest({
          config: {
            method: 'get',
            url: '/api/c2_homepage/infl_window_object'
          }
        }),
        TE.map(x => {
          window._infl = x.data as {
            currentContact?: { id: string; name: string; email: string; isAdmin: boolean };
            hub?: { id: string; features?: string[]; subdomain?: string };
          };
          setInflData(x.data as any);
        }),
        invokeTask => invokeTask()
      );
    }
  }, [setInflData]);

  useEffect(() => {
    Userpilot.initialize(USERPILOT_TOKEN);
    if (typeof window !== 'undefined' && window?._infl?.currentContact) {
      Userpilot.identify(window._infl.currentContact!.id, {
        name: window._infl.currentContact.name,
        email: window._infl.currentContact.email,
        userID: window._infl.currentContact!.id,
        isAdmin: window._infl.currentContact.isAdmin,
        company: {
          id: window?._infl?.hub?.id,
          name: window?._infl?.hub?.subdomain
        }
      });
    }
  }, []);

  const helmet = isHelmetContextFilled(helmetContext) ? helmetContext.helmet : null;

  return (
    <HelmetProvider context={helmetContext}>
      {typeof window !== 'undefined' &&
        window?._infl?.hub?.features?.some(x => x === 'complete_tracing_enabled') && (
          <DataDog
            config={pipe(host ?? '', host => ({
              service: 'advocate-next/fe',
              applicationId: DD_APP_ID,
              clientToken: DD_CLIENT_TOKEN,
              version: RELEASE_VERSION,
              env: {
                production: 'prod' as const,
                staging: 'staging' as const,
                development: 'development' as const
              }[DEPLOY_ENV],
              excludedActivityUrls: [
                `${host}/forum/message-bus/*`,
                `${host}/xhr_send*`,
                `${host}/_challenge_preview*`,
                `wss://${host}/graphql`,
                'wss://chat.stream-io-api.com/connect*'
              ]
            }))}
          />
        )}
      <Head>
        {helmet ? <>{helmet.title.toComponent()}</> : null}
        {helmet ? <>{helmet.meta.toComponent()}</> : null}
        {helmet ? <>{helmet.link.toComponent()}</> : null}
        {helmet ? <>{helmet.script.toComponent()}</> : null}
      </Head>
      <SetupApp deviceType={deviceType} apolloClient={client} Router={router(routerContext)}>
        <ErrorBoundary FallbackComponent={FallbackError}>
          <Component {...pageProps} />
        </ErrorBoundary>
      </SetupApp>
    </HelmetProvider>
  );
};

App.getInitialProps = async (ctx: AppContext) => {
  const props = await NextApp.getInitialProps(ctx);
  if (typeof window !== 'undefined') return props;

  const { req, res } = ctx.ctx;
  if (!req || !res)
    throw new Error('Unreachable: req and res should always be present on server-side');

  const {
    headers,
    headers: { host, 'user-agent': userAgent }
  } = req;
  const routerContext: StaticRouterContext = {};
  const apolloErrorContext: ApolloQueryError[] = [];
  const client = initializeApollo({
    host,
    headers,
    errorContext: apolloErrorContext,
    authErrorCallback: logoutCallback
  })();

  const [{ getDataFromTree }, { default: parser }] = await Promise.all([
    import('@apollo/client/react/ssr'),
    import('ua-parser-js')
  ]);
  const deviceType = parser(userAgent).device.type;

  await getDataFromTree(
    <ctx.AppTree
      {...props}
      host={host}
      deviceType={deviceType}
      apolloClient={client}
      routerContext={routerContext}
    />
  );

  return pipe(
    A.head(apolloErrorContext),
    O.match(constant(unitIOE), ({ statusCode, redirectUrl }) =>
      pipe(
        setStatusCode(res, statusCode),
        IOE.apSecond(redirectWithUrl(res, statusCode, redirectUrl))
      )
    ),
    IOE.apSecond(IOE.right(routerContext)),
    IOE.chainW(({ statusCode, url }) =>
      pipe(setStatusCode(res, statusCode), IOE.apSecond(redirectWithUrl(res, statusCode, url)))
    ),
    IOE.apSecond(IOE.rightIO(() => client.extract())),
    IOE.map(apolloState => ({ ...props, host, deviceType, apolloState })),
    IOE.getOrElseW(O.match(constant(constVoid), err => () => throwFn(err)))
  )();
};

export default App;
